import React, { useState, useRef } from 'react';
// Use the lightweight YouTube build of ReactPlayer (includes only YouTube code)
import ReactPlayer from 'react-player';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { RADIO_STREAMS, DRUM_BREAKS, DJ_MIXES } from '../data/radioData';
import { Radio } from 'lucide-react';

// Detect both youtube.com and youtu.be links so they are handled by ReactPlayer
const isYouTube = (url) => url && /(youtube\.com|youtu\.be)/.test(url);

const RadioPlayer = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const [current, setCurrent] = useState(null);
  const [volume, setVolume] = useState(0.8);
  const [currentTitle, setCurrentTitle] = useState('');
  const [playerError, setPlayerError] = useState(null);
  const timeoutRef = useRef(null);

  const handlePlayPause = (url, title) => {
    if (current?.url === url) {
      setPlaying(!playing);
    } else {
      setPlaying(false);
      clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(() => {
        setCurrent({ url, type: 'radio' });
        setCurrentTitle(title || '');
        setPlaying(true);
      }, 200); // 200ms de délai pour laisser le player démonter proprement
    }
  };

  if (!isOpen) {
    return (
      <button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-4 right-4 bg-gradient-to-br from-pink-500 to-yellow-400 text-white p-4 rounded-full shadow-2xl z-50 flex items-center justify-center hover:scale-110 transition-transform border-4 border-black"
        style={{ fontSize: 36 }}
        aria-label="Open Crewmate Radio"
      >
        <Radio className="w-8 h-8" />
      </button>
    );
  }

  return (
    <div className="fixed bottom-4 right-4 w-96 max-w-sm z-50 overflow-y-auto max-h-[90vh]">
      <Card className="bg-white text-black border-4 border-black">
        <CardContent className="p-4">
          <div className="flex justify-between items-center mb-4">
            <h3 className="font-bold flex items-center gap-2"><Radio className="w-5 h-5 text-pink-500" />Crewmate Radio</h3>
            <div className="flex items-center gap-1">
              <Button onClick={() => setCollapsed(!collapsed)} size="sm" variant="ghost" title={collapsed ? 'Expand' : 'Minimize'}>
                {collapsed ? '+' : '−'}
              </Button>
              <Button onClick={() => setIsOpen(false)} size="sm" variant="ghost" title="Close">✖️</Button>
            </div>
          </div>
          {/* UI (tabs, volume) hidden when collapsed */}
          {!collapsed && (
            <>
              <Tabs defaultValue="radios" className="w-full">
                <TabsList className="grid w-full grid-cols-3">
                  <TabsTrigger value="radios">Radios</TabsTrigger>
                  <TabsTrigger value="breaks">Breaks</TabsTrigger>
                  <TabsTrigger value="mixes">Mixes</TabsTrigger>
                </TabsList>
                <TabsContent value="radios" className="mt-4 max-h-60 overflow-y-auto">
                  <div className="space-y-2">
                    {RADIO_STREAMS.map((radio, i) => (
                      <button key={i} onClick={() => handlePlayPause(radio.url, radio.name)} className="w-full text-left p-2 rounded hover:bg-gray-100">
                        {radio.name}
                      </button>
                    ))}
                  </div>
                </TabsContent>
                <TabsContent value="breaks" className="mt-4 max-h-60 overflow-y-auto">
                  <div className="space-y-2">
                    {DRUM_BREAKS.map((track, i) => (
                      <button key={i} onClick={() => handlePlayPause(track.url, track.title)} className="w-full text-left p-2 rounded hover:bg-gray-100">
                        {track.title}
                      </button>
                    ))}
                  </div>
                </TabsContent>
                <TabsContent value="mixes" className="mt-4 max-h-60 overflow-y-auto">
                  <div className="text-gray-400 text-center">Coming soon: DJ mixes!</div>
                </TabsContent>
              </Tabs>
              <div className="mt-4 flex flex-col items-center gap-2">
                {current && (
                  <div className="w-full flex flex-col items-center mb-2">
                    <div className="font-bold text-center text-black truncate w-full" title={currentTitle}>{currentTitle}</div>
                    <Button onClick={() => setPlaying(!playing)} className="mt-2 bg-pink-500 text-white font-bold px-6 py-2 rounded-full border-2 border-black hover:bg-pink-600">
                      {playing ? 'Pause' : 'Play'}
                    </Button>
                  </div>
                )}
                <input type="range" min="0" max="1" step="0.05" value={volume} onChange={e => setVolume(parseFloat(e.target.value))} className="w-full" />
              </div>
            </>
          )}
          {/* Player – always rendered to keep audio even when collapsed */}
          <div className="w-full mt-2" style={{ display: collapsed ? 'none' : 'block' }}>
            {playerError && (
              <div className="bg-red-100 text-red-700 border border-red-400 rounded p-2 mb-2 text-center text-sm font-bold">
                Erreur de chargement du player YouTube.<br />
                Vérifie ta connexion, les extensions de blocage, ou la politique de sécurité du navigateur (CSP).<br />
                Détail : {playerError}
              </div>
            )}
            {current && !isYouTube(current.url) ? (
              <audio controls autoPlay src={current.url} style={{ width: '100%' }} />
            ) : current && isYouTube(current.url) ? (
              <>
                <ReactPlayer
                  key={current.url}
                  src={current.url}
                  playing={playing}
                  volume={volume}
                  width="100%"
                  height="360px"
                  controls
                  config={{
                    youtube: {
                      playerVars: {
                        autoplay: playing ? 1 : 0,
                        modestbranding: 1,
                        rel: 0,
                        origin: window.location.origin
                      }
                    }
                  }}
                  style={{ background: '#000' }}
                  onError={e => { setPlayerError(e?.toString?.() || 'Erreur inconnue'); console.error('ReactPlayer error', e); }}
                  onReady={() => { console.log('ReactPlayer ready for', current.url); }}
                />
                <div className="text-xs text-gray-500 mt-2 text-center">
                  If you don't hear sound, click Play in the YouTube player below.<br />
                  (Some security policies or browser extensions may block the display)
                </div>
              </>
            ) : null}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default RadioPlayer; 